<template>
  <Teleport to="body">
    <Transition name="sheet">
      <div
        v-if="show"
        class="fixed inset-0 z-50 flex flex-col justify-end"
        role="dialog"
        aria-modal="true"
        aria-label="Call Recording"
      >
        <!-- Scrim -->
        <div class="absolute inset-0 bg-black/40" @click="$emit('close')" />

        <!-- Sheet panel -->
        <div class="relative w-full rounded-t-2xl bg-white dark:bg-slate-900">
          <!-- Drag handle -->
          <div class="flex justify-center pt-3 pb-1">
            <div class="h-1 w-10 rounded-full bg-gray-300" />
          </div>

          <!-- Header -->
          <div class="flex items-center justify-between px-4 py-3">
            <div>
              <h2 class="text-base font-semibold text-gray-900 dark:text-slate-100">Call Recording</h2>
              <p class="text-xs text-gray-500 dark:text-slate-400">{{ callLabel }}</p>
            </div>
            <button
              class="flex h-8 w-8 items-center justify-center rounded-full text-gray-400 transition hover:bg-gray-100 dark:hover:bg-slate-700 hover:text-gray-600 dark:hover:text-slate-300"
              aria-label="Close"
              @click="$emit('close')"
            >
              <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <!-- Content -->
          <div class="px-4 pb-8">
            <!-- Loading URL -->
            <div v-if="isLoadingUrl" class="flex flex-col items-center gap-3 py-8">
              <div class="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              <p class="text-sm text-gray-500 dark:text-slate-400">Loading recording…</p>
            </div>

            <!-- Error / unavailable -->
            <div v-else-if="!audioUrl" class="py-8 text-center">
              <p class="text-sm text-gray-400">Recording unavailable.</p>
            </div>

            <!-- Player -->
            <template v-else>
              <!-- Native audio element (hidden — we build custom controls on top) -->
              <audio
                ref="audioEl"
                :src="audioUrl"
                preload="metadata"
                @loadedmetadata="onMetadata"
                @timeupdate="onTimeUpdate"
                @ended="onEnded"
                @pause="isPlaying = false"
                @play="isPlaying = true"
              />

              <!-- Player card -->
              <div class="rounded-xl border border-gray-100 dark:border-slate-800 bg-gray-50 dark:bg-slate-800 p-4">
                <!-- Seek slider -->
                <div class="mb-3">
                  <input
                    ref="scrubberEl"
                    type="range"
                    class="w-full accent-primary"
                    :min="0"
                    :max="duration || 100"
                    :disabled="!duration"
                    @input="seek"
                  >
                  <div class="mt-1 flex justify-between text-xs text-gray-400">
                    <span>{{ formatTime(currentTime) }}</span>
                    <span>{{ formatTime(duration) }}</span>
                  </div>
                </div>

                <!-- Controls -->
                <div class="flex items-center justify-center gap-4">
                  <!-- Stop -->
                  <button
                    class="flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-gray-700 dark:text-slate-300 transition hover:bg-gray-100 dark:hover:bg-slate-700 disabled:opacity-40"
                    :disabled="!duration"
                    aria-label="Stop"
                    @click="stop"
                  >
                    <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                      <rect x="5" y="5" width="14" height="14" rx="2" />
                    </svg>
                  </button>

                  <!-- Play / Pause -->
                  <button
                    class="flex h-14 w-14 items-center justify-center rounded-full bg-primary text-white shadow-md transition hover:opacity-90 disabled:opacity-40"
                    :disabled="!audioUrl"
                    :aria-label="isPlaying ? 'Pause' : 'Play'"
                    @click="togglePlay"
                  >
                    <!-- Play icon -->
                    <svg v-if="!isPlaying" class="ml-1 h-7 w-7" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                    <!-- Pause icon -->
                    <svg v-else class="h-7 w-7" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
                    </svg>
                  </button>
                </div>
              </div>

              <!-- Download button -->
              <div class="mt-4 flex justify-center">
                <a
                  :href="audioUrl"
                  download
                  class="flex items-center gap-2 rounded-xl border border-gray-200 dark:border-slate-700 px-5 py-2.5 text-sm font-medium text-gray-700 dark:text-slate-300 transition hover:bg-gray-50 dark:hover:bg-slate-800"
                >
                  <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download
                </a>
              </div>
            </template>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { getApp } from 'firebase/app';
import { getStorage, ref as storageRef, getDownloadURL } from 'firebase/storage';
import type { Auth } from 'firebase/auth';
import type { CallRecord } from '~/types/calls';
import { isCallInbound } from '~/utils/calls';
import { useContactStore } from '~/stores/contactStore';

const props = defineProps<{
  show: boolean;
  call: CallRecord | null;
}>();

defineEmits<{ close: [] }>();

const contactStore = useContactStore();

const { $auth } = useNuxtApp();
const runtimeConfig = useRuntimeConfig();

const audioEl = ref<HTMLAudioElement | null>(null);
const scrubberEl = ref<HTMLInputElement | null>(null);
const audioUrl = ref<string | null>(null);
const isLoadingUrl = ref(false);
const isPlaying = ref(false);
const currentTime = ref(0);
const duration = ref(0);

const callLabel = computed(() => {
  const call = props.call;
  if (!call) return '';
  const number = isCallInbound(call)
    ? (call.callerNumber || call.callerId || '')
    : (call.destinationNumber || '');
    
  if (number) {
    const contactName = contactStore.getNameForNumber(number);
    if (contactName) return `${contactName} (${number})`;
  }
  return number;
});

function formatTime(seconds: number): string {
  const s = Math.floor(seconds);
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
}

function togglePlay() {
  if (!audioEl.value) return;
  if (isPlaying.value) {
    audioEl.value.pause();
  } else {
    audioEl.value.play().catch(() => {});
  }
}

function stop() {
  if (!audioEl.value) return;
  audioEl.value.pause();
  audioEl.value.currentTime = 0;
  isPlaying.value = false;
}

function seek(event: Event) {
  const input = event.target as HTMLInputElement;
  if (!audioEl.value) return;
  audioEl.value.currentTime = Number(input.value);
}

function onMetadata() {
  duration.value = audioEl.value?.duration ?? 0;
  if (scrubberEl.value) {
    // Set max before value — if value > new max the browser clamps it to max,
    // which would make the scrubber appear stuck at the end before playback starts.
    scrubberEl.value.max = String(duration.value);
    scrubberEl.value.value = '0';
  }
}

function onTimeUpdate() {
  const t = audioEl.value?.currentTime ?? 0;
  currentTime.value = t;
  // Drive the scrubber position directly — Vue's :value binding can lag after
  // a user seek because the browser marks the element dirty and skips updates.
  if (scrubberEl.value) scrubberEl.value.value = String(t);
}

// Called when playback reaches the end: pause, reset to start, and move the
// scrubber back to position 0.
function onEnded() {
  isPlaying.value = false;
  currentTime.value = 0;
  if (audioEl.value) audioEl.value.currentTime = 0;
  if (scrubberEl.value) scrubberEl.value.value = '0';
}

/** Resolves a Firebase Storage download URL for a call recording.
 * The RTDB record never stores recording.bucket/objectName (they are stripped
 * before the RTDB write). When only rec.ts is present we derive the object
 * path from the known storage layout used by the moveRecordings cloud function:
 *   user/{uid}/callSessionId/{callId}/recording.wav
 */
async function resolveRecordingUrl(call: CallRecord): Promise<string | null> {
  let bucket = call.recording?.bucket ?? null;
  let objectName = call.recording?.objectName ?? null;

  // Derive path from RTDB marker when bucket/objectName are absent (the common case)
  if (!objectName && call.rec?.ts) {
    const uid = ($auth as Auth).currentUser?.uid;
    if (!uid) return null;
    objectName = `user/${uid}/callSessionId/${call.id}/recording.wav`;
    bucket = bucket || runtimeConfig.public.callRecordingsBucket || null;
  }

  if (!objectName || !bucket) return null;

  try {
    const app = getApp();
    const normalizedBucket = bucket.startsWith('gs://') ? bucket : `gs://${bucket}`;
    const storage = getStorage(app, normalizedBucket);
    return await getDownloadURL(storageRef(storage, objectName));
  } catch (err) {
    console.error('Failed to resolve recording URL', err);
    return null;
  }
}

watch(() => props.show, async (visible) => {
  if (!visible) {
    // Stop playback and reset state when sheet closes
    stop();
    audioUrl.value = null;
    currentTime.value = 0;
    duration.value = 0;
    return;
  }

  const call = props.call;
  if (!call) return;

  const hasRecording = call.rec?.ts || call.recording?.objectName;
  if (!hasRecording) return;

  isLoadingUrl.value = true;
  audioUrl.value = await resolveRecordingUrl(call);
  isLoadingUrl.value = false;
});
</script>

<style scoped>
.sheet-enter-active,
.sheet-leave-active {
  transition: opacity 0.25s ease;
}
.sheet-enter-active .relative,
.sheet-leave-active .relative {
  transition: transform 0.3s ease;
}
.sheet-enter-from,
.sheet-leave-to {
  opacity: 0;
}
.sheet-enter-from .relative {
  transform: translateY(100%);
}
.sheet-leave-to .relative {
  transform: translateY(100%);
}
</style>
